/**
 * This function adds the `level`, `treeName` and the `children` array for each classification
 * and sort them by putting parent and children contiguous. So you can think if two
 * classifications are contiguous in the array they are brothers or the second is
 * child of the first (if the second is a child of the first it will have level bigger than the
 * first).
 * 
 * @param {Object[]} classifications with id, name and parentId.
 * @returns {Object[]} with the classifications sorted by name, familiarity and level.
 */
export function makeTree(classifications) {
  /* Add the children array */
  classifications = classifications.map(c => ({ ...c, level: 0, children: [] }))

  /**
   * Increase by 1 the level of the given classification and recursively the level of its children.
   * @param {Object} classification The classification to increase it's level
   */
  function increase_level(classification) {
    classification.level += 1
    for (let child of classification.children) {
      increase_level(child)
    }
  }

  /* Add the children for each classification */
  classifications.forEach(child => {
    let parent = classifications.find(c => c.id === child.parentId)
    if (parent) {
      parent.children.push(child)
      increase_level(child)
    }
  })

  /**
   * Flat the given classifications by recursively calling the flat method over
   * the children of the current classification.
   * Also, it adds the 'treeName' property to visualize the depth of the classification.
   */
  function flat(classifications) {
    return classifications.reduce(
      (flatten, c) => [
        ...flatten,
        { ...c, treeName: `${"- ".repeat(c.level)}${c.name} (${c.id})` },
        ...flat(c.children)
      ],
      []
    );
  }

  /* Get only the classifications of level 0 to flat the list */
  return flat(classifications.filter(c => c.level === 0))
}